import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import moment from "moment";

@Component({
	selector: "app-cbta-datetime-picker",
	templateUrl: "./cbta-datetime-picker.component.html",
	styleUrls: ["./cbta-datetime-picker.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CbtaDatetimePickerComponent implements OnInit, OnChanges, AfterViewInit {
	@Input() placeHolder = "Select date and time";
	@Input() label = "";
	@Input() isDisabled = false;
	@Input() format = "DD MMM YYYY, h:mm A";
	@Input() defaultValue?: Date | string | null;
	@Input() minDate?: Date | string | null;
	@Input() errorMessage = "";
	@Input() stepMinute = 15;
	@Input() showClear = false;

	@Input() datePickerInput = new UntypedFormControl(null);

	@ViewChild("datePicker") datePicker?: { nativeElement: HTMLInputElement };

	ngOnInit(): void {
		this.syncDisabledState();
		this.applyInitialDate();
		this.datePickerInput.valueChanges.subscribe((value) => {
			this.updateDisplayDateFormat(this.toDate(value));
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["isDisabled"]) this.syncDisabledState();
		if (changes["defaultValue"] && !changes["defaultValue"].firstChange) {
			this.applyInitialDate();
		}
		if (changes["format"] && !changes["format"].firstChange) {
			this.updateDisplayDateFormat(this.datePickerInput.value);
		}
	}

	constructor(private readonly cd: ChangeDetectorRef) {}

	ngAfterViewInit(): void {
		this.updateDisplayDateFormat(this.datePickerInput.value);
	}

	handleClose(raw: Date | string | null | undefined): void {
		const next = this.snapToMinuteStep(this.toDate(raw));
		if (!next) return;
		this.datePickerInput.patchValue(next, { emitEvent: true });
		this.updateDisplayDateFormat(next);
	}

	clearValue(): void {
		this.datePickerInput.patchValue(null, { emitEvent: true });
		this.updateDisplayDateFormat(null);
	}

	get normalizedMin(): Date | null {
		return this.toDate(this.minDate);
	}

	get startAt(): Date | null {
		return this.toDate(this.defaultValue) ?? (this.datePickerInput.value as Date | null) ?? null;
	}

	private applyInitialDate(): void {
		const initial =
			this.toDate(this.defaultValue) ?? this.toDate(this.datePickerInput.value);
		if (initial !== null || !this.datePickerInput.value) {
			this.datePickerInput.patchValue(initial, { emitEvent: false });
		}
		this.updateDisplayDateFormat(this.toDate(this.datePickerInput.value));
	}

	private syncDisabledState(): void {
		if (this.isDisabled) this.datePickerInput.disable({ emitEvent: false });
		else this.datePickerInput.enable({ emitEvent: false });
	}

	private updateDisplayDateFormat(date: Date | null): void {
		const el = this.datePicker?.nativeElement;
		if (!el) return;
		el.value = date ? moment(date).format(this.format) : "";
		this.cd.detectChanges();
	}

	private snapToMinuteStep(date: Date | null): Date | null {
		if (!date || this.stepMinute <= 1) return date;
		const snapped = moment(date).seconds(0).milliseconds(0);
		const minute = snapped.minute();
		const roundedMinute = Math.round(minute / this.stepMinute) * this.stepMinute;
		if (roundedMinute >= 60) {
			snapped.add(1, "hour").minute(0);
		} else {
			snapped.minute(roundedMinute);
		}
		return snapped.toDate();
	}

	private toDate(value: Date | string | null | undefined): Date | null {
		if (!value) return null;
		if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

		const strict = moment(value, [moment.ISO_8601, "YYYY-MM-DDTHH:mm", "YYYY-MM-DDTHH:mm:ss", this.format], true);
		if (strict.isValid()) return strict.toDate();

		const loose = moment(value);
		return loose.isValid() ? loose.toDate() : null;
	}
}
